import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { SpecialOfferLabel } from '@/components/atoms/SpecialOfferLabel/SpecialOfferLabel';
import { H1 } from '@/components/atoms/Typography/Typography';
import { SpecialOfferFootnote } from '@/components/local/SpecialOfferFootnote/SpecialOfferFootnote';
import { SpecialOfferLabelsGroup } from '@/components/molecules/SpecialOfferLabelsGroup/SpecialOfferLabelsGroup';
import nameSpacesConfig from '@/config/config.nameSpaces.json';
import { ChooseMembership } from '@/containers/ConfigurePage/components/ChooseMembership/ChooseMembership';
import { CostSummary } from '@/containers/ConfigurePage/components/CostSummary/CostSummary';
import {
  FormConfig,
  FormConnector,
} from '@/containers/ConfigurePage/components/FormConfig/FormConfig';
import { ConfigureAddToCartGTM } from '@/lib/gtm/gtmComponents';
import { getCountryConfigFromLocale } from '@/lib/i18n/getCountryConfigFromLocale';
import { useData } from '@/lib/pageData/pageDataContext';
import { checkGate, FEATURE_GATES, useLogEvent } from '@/lib/statsig';
import { CityData } from '@/types/CityData';
import type { SubscriptionAddon } from '@/types/Subscription';
import { TransformedAssetCategoryResult } from '@/types/TransformedAssetCategoryResults';
import {
  ProductSubscriptionByRegionResponse,
  subscriptionCategories,
} from '@/utils/fetch/fetchProductSubscriptionByRegion';
import { getSubscriptionAddon } from '@/utils/getSubscriptionAddon';
import { getProductStudentPrice } from '@/utils/productPrice';
import { isThreeTierProduct } from '@/utils/threeTier';
import { AreYouStudent } from './components/AreYouStudent/AreYouStudent';
import { BasketAddon } from './components/BasketAddon/BasketAddon';
import { BikeImage } from './components/BikeImage/BikeImage';
import { ChooseMembershipThreeTier } from './components/ChooseMembershipThreeTier/ChooseMembershipThreeTier';
import { Footnote } from './components/Footnote/Footnote';
import { HeavyUseDisabledLabel } from './components/HeavyUseDisabledLabel/HeavyUseDisabledLabel';
import { HeavyUser } from './components/HeavyUser/HeavyUser';
import { OrderBikeButton } from './components/OrderBikeButton/OrderBikeButton';
import styles from './ConfigurePage.module.scss';
import { BASKET_STORAGE_KEY } from './constants';
import { getCurrentSubscriptionPromoLabelI18nKey } from './utils/getCurrentSubscriptionPromoLabelI18nKey';
import { getHeavyUseRestrictionProductId } from './utils/getHeavyUseRestrictionProductId';
import { getSelectedSubscription } from './utils/getSelectedSubscription';

export interface ConfigurePageProps {
  currentCity: CityData;
  product: TransformedAssetCategoryResult;
  subscriptionAddons?: SubscriptionAddon[];
  subscriptions: ProductSubscriptionByRegionResponse;
}

export const ConfigurePageComponent = ({
  product,
  currentCity,
  subscriptions,
  subscriptionAddons,
}: ConfigurePageProps) => {
  const { locale, query } = useRouter();
  const { currency } = useData<'default'>();
  const [isBasketOptIn, setIsBasketOptIn, { removeItem: removeIsBasketOptIn }] =
    useLocalStorageState<boolean>(BASKET_STORAGE_KEY);

  const { availabilityState, productConfig } = product;
  useLogEvent('Product configure page', {
    assetName: productConfig.name,
  });

  const { countryCode } = getCountryConfigFromLocale(locale!);
  const { t } = useTranslation([
    nameSpacesConfig.common,
    nameSpacesConfig.pages,
  ]);

  const isThreeTier = isThreeTierProduct(
    countryCode,
    product.assetCategoryCode
  );

  const hasSpecialOffer = Boolean(product.rates.specialOffer);
  const hasReducedPrice = checkGate(
    {
      custom: {
        country: countryCode,
        product: productConfig.id,
      },
    },
    FEATURE_GATES.priceReduction
  );

  const isBasketAddonEnabled = checkGate(
    {
      custom: {
        city: query.city,
        product: productConfig.id,
        region: currentCity.region.toLowerCase(),
      },
    },
    FEATURE_GATES.basketAddon
  );
  const basketAddon = getSubscriptionAddon('basket', subscriptionAddons);
  const showBasketAddon =
    isBasketAddonEnabled &&
    basketAddon &&
    product.productConfig.id !== 'power-plus';

  useEffect(() => {
    removeIsBasketOptIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const heavyUseRestrictionProductId = getHeavyUseRestrictionProductId(
    countryCode,
    query.city as string,
    productConfig
  );
  const isHeavyUseDisabled = heavyUseRestrictionProductId !== undefined;
  const isHeavyUseProduct = !!product.rates.heavyUse && !product.rates.normal;

  const showHeavyUserSubform =
    !isHeavyUseProduct &&
    !!subscriptions.filter(
      sub => sub.customerType === subscriptionCategories.heavyUse
    ).length;
  const showStudentSubform = !!subscriptions.filter(
    sub => sub.customerType === subscriptionCategories.student
  ).length;
  const ChooseMembershipComponent = isThreeTier
    ? ChooseMembershipThreeTier
    : ChooseMembership;

  return (
    <FormConfig
      subscriptions={subscriptions}
      className={styles.container}
      city={currentCity}
      product={product}
      isHeavyUseDisabled={isHeavyUseDisabled}
    >
      <ConfigureAddToCartGTM
        city={currentCity}
        product={product}
        subscriptions={subscriptions}
      />
      <div className={styles.wrapper}>
        <div className={styles.wrapperLeft}>
          <FormConnector>
            {values => {
              const currentSubscription = getSelectedSubscription({
                currentFormValues: values,
                product,
                subscriptions,
              });
              const studentLabelI18nKey =
                currentSubscription?.customerType === 'student' &&
                Boolean(getProductStudentPrice(product.rates))
                  ? 'common:specialOffers.student'
                  : '';
              const promoLabelI18nKey = getCurrentSubscriptionPromoLabelI18nKey(
                countryCode,
                product,
                values.durationMonths,
                currentSubscription?.code
              );

              return (
                <BikeImage
                  productConfig={productConfig}
                  imageSrc={
                    isBasketOptIn && productConfig.imageBasket
                      ? productConfig.imageBasket
                      : productConfig.imageConfigure
                  }
                  label={
                    <SpecialOfferLabelsGroup className={styles.labelsGroup}>
                      {promoLabelI18nKey && (
                        <SpecialOfferLabel
                          text={t(promoLabelI18nKey)}
                          color={hasReducedPrice ? 'secondary' : 'primary'}
                        />
                      )}
                      {studentLabelI18nKey && (
                        <SpecialOfferLabel
                          color="lightBlue"
                          text={t(studentLabelI18nKey)}
                        />
                      )}
                    </SpecialOfferLabelsGroup>
                  }
                />
              );
            }}
          </FormConnector>
        </div>
        <div className={styles.wrapperRight}>
          <section className={styles.form} data-test-id="configure-page-form">
            <H1 version="level2" className={styles.header}>
              {t(
                `pages:configure.header.${
                  !isThreeTier ? 'headingMain' : 'headingTest'
                }`,
                {
                  product: productConfig.uiName,
                }
              )}
            </H1>
            <section className={styles.formContainer}>
              <ChooseMembershipComponent
                hasSpecialOffer={hasSpecialOffer}
                subscriptions={subscriptions}
                product={product}
              />
              {showBasketAddon && (
                <BasketAddon
                  imageSrc={productConfig.imageBasketMini!}
                  onChange={setIsBasketOptIn}
                  price={basketAddon.price}
                />
              )}
              {showStudentSubform && (
                <AreYouStudent
                  product={product}
                  subscriptions={subscriptions}
                />
              )}
              {showHeavyUserSubform && (
                <HeavyUser
                  subscriptions={subscriptions}
                  disabledLabel={
                    isHeavyUseDisabled ? (
                      <HeavyUseDisabledLabel
                        currency={currency}
                        locale={locale!}
                        productId={heavyUseRestrictionProductId}
                      />
                    ) : undefined
                  }
                />
              )}
              <FormConnector>
                {({ heavyUse }) => {
                  if (isHeavyUseDisabled && heavyUse === 'yes') {
                    return null;
                  }

                  return (
                    <CostSummary
                      product={product}
                      subscriptionAddons={subscriptionAddons}
                      subscriptions={subscriptions}
                    />
                  );
                }}
              </FormConnector>
            </section>
            <div className={styles.orderBikeBtnContainer}>
              <OrderBikeButton
                availabilityState={availabilityState}
                disabled={isHeavyUseDisabled}
              />
              <div className={styles.btnUnderlay} />
            </div>
            {isThreeTier && (
              <Footnote product={product} subscriptions={subscriptions} />
            )}
            {hasSpecialOffer && (
              <SpecialOfferFootnote
                i18nKey={`product:${productConfig.translationKey}.specialOffer.extendedFootnote`}
                productKey={productConfig.id}
              />
            )}
          </section>
        </div>
      </div>
    </FormConfig>
  );
};
